import type { BulkSelectionMode } from '@/composables/useBulkSelection';

/**
 * The schedule selection the custom list search keeps next to its list selection.
 * The bulk bar reads it to say what is selected and to offer "112 schedules".
 */
export interface BulkBarScheduleSelection {
  mode: BulkSelectionMode;
  /** How many schedules a bulk action would cover right now. */
  count: number;
  /** Schedules unticked after "select all matching". */
  excludedCount: number;
  /** How many schedules pass the search. Null until the count endpoint answers. */
  total: number | null;
  /** True when the count endpoint failed. */
  totalFailed: boolean;
  /** Whether "112 schedules" would add schedules to the selection. */
  canSelectAllMatching: boolean;
}

/** One kind's share of a selection, such as the lists or the schedules. */
export interface BulkSelectionKind {
  mode: BulkSelectionMode;
  count: number;
  excludedCount: number;
  singular: string;
  plural: string;
}

/**
 * One kind's part of the selected text.
 *
 * Examples: "3 lists", "1 schedule", "all 40 lists", and "111 of 112 schedules" once one
 * schedule was unticked after "select all matching".
 */
function kindPhrase(kind: BulkSelectionKind): string {
  if (kind.mode === 'search' && kind.excludedCount > 0) {
    return `${kind.count.toLocaleString()} of ${(kind.count + kind.excludedCount).toLocaleString()} ${kind.plural}`;
  }
  const noun = kind.count === 1 ? kind.singular : kind.plural;
  const prefix = kind.mode === 'search' ? 'all ' : '';
  return `${prefix}${kind.count.toLocaleString()} ${noun}`;
}

/**
 * The bar's selected text when two kinds can be ticked, such as "3 lists, 14 schedules selected".
 * A kind with nothing selected is left out, so one ticked schedule reads "1 schedule selected".
 */
export function twoKindSelectionText(kinds: BulkSelectionKind[]): string {
  const phrases = kinds.filter((kind) => kind.count > 0).map(kindPhrase);
  const text = `${phrases.join(', ')} selected`;
  return text.charAt(0).toUpperCase() + text.slice(1);
}
